import { useEffect, useState } from 'react';
import { api, CallMessage } from '../../api';
import { useIsMobile } from '../../lib/media';
import { useBackEntry } from '../../nav/useBackEntry';
import { PanelHead } from '../../nav/PanelHead';
import { IconFile } from '../../components/icons';

type SummaryFile = { id: string; name: string; size: number };

const fmtSize = (n: number): string =>
  n < 1024 ? `${n} Б` : n < 1024 * 1024 ? `${Math.round(n / 1024)} КБ` : `${(n / 1024 / 1024).toFixed(1)} МБ`;

/*
 * Итоги звонка: открывается из уведомления «Итоги звонка» (openNotification.ts)
 * и доступно в любом разделе. Раскладка — та же пара, что у выбора людей в
 * звонок (InviteDialog.tsx): на десктопе окно, на телефоне экран во всю
 * высоту (.call-pick). Заворачивать окно в .nav-layer нельзя: подложка
 * .backdrop внутри слоя остаётся фиксированной и на телефоне даёт всё тот же
 * попап по центру, а правило стопки требует экрана.
 *
 * Две категории файлов: собранные на доске и приложенные к переписке
 * (решение Ильи 15.09.2026). Самой переписки здесь нет — итог про то, что
 * из звонка осталось на руках, а не про то, кто что написал. Место под
 * протокол встречи оставлено структурно: это будет соседний блок здесь же.
 */
export function CallSummary({ callId, onClose }: { callId: string; onClose: () => void }) {
  const mobile = useIsMobile();
  const [files, setFiles] = useState<SummaryFile[] | null>(null);
  /** Файлы были, но семь дней вышли и их убрали (решение Ильи 15.09.2026). */
  const [expired, setExpired] = useState(false);
  /** Переписка звонка: после его конца она читается отсюда (спека 5). */
  const [messages, setMessages] = useState<CallMessage[] | null>(null);
  const [error, setError] = useState('');

  useBackEntry(mobile, onClose);

  useEffect(() => {
    let alive = true;
    api
      .callSummary(callId)
      .then((r) => {
        if (!alive) return;
        setFiles(r.files);
        setExpired(r.expired);
      })
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Не удалось загрузить итоги'));
    // Переписки может не быть вовсе — тогда блок просто не показывается.
    api
      .callMessages(callId)
      .then((list) => alive && setMessages(list))
      .catch(() => alive && setMessages([]));
    return () => {
      alive = false;
    };
  }, [callId]);

  // Файлы переписки берутся из самих сообщений: отдельного списка на сервере
  // нет, а вложение всегда живёт при сообщении.
  const chatFiles = (messages ?? []).flatMap((m) => m.files.map((f) => ({ file: f, at: m.at })));

  const body = (
    <>
      {error && <div className="banner crit">{error}</div>}
      {files && (
        <div className="field">
          <span className="field-label">Файлы доски</span>
          {files.length ? (
            <div className="row-list">
              {files.map((f) => (
                <a key={f.id} className="row static" href={api.callFileUrl(callId, f.id)} download={f.name}>
                  <span className="ec-ico">
                    <IconFile />
                  </span>
                  <span className="row-text">
                    <span className="row-title">{f.name}</span>
                    <span className="row-sub">{fmtSize(f.size)}</span>
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <p className="sub">{expired ? 'Файлы больше недоступны: срок хранения семь дней' : 'Листов на доске не было'}</p>
          )}
        </div>
      )}
      {chatFiles.length > 0 && (
        <div className="field">
          <span className="field-label">Файлы из переписки</span>
          <div className="row-list">
            {chatFiles.map(({ file }) => (
              <a
                key={file.id}
                className="row static"
                href={api.callMessageFileUrl(callId, file.id)}
                download={file.name}
              >
                <span className="ec-ico">
                  <IconFile />
                </span>
                <span className="row-text">
                  <span className="row-title">{file.name}</span>
                  <span className="row-sub">{fmtSize(file.size)}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const actions = (
    <div className="dialog-foot">
      <button className="btn primary" onClick={onClose}>
        Закрыть
      </button>
    </div>
  );

  if (mobile) {
    return (
      <div className="call-pick">
        <PanelHead title="Итоги звонка" onBack={onClose} />
        <div className="call-pick-body">{body}</div>
        {actions}
      </div>
    );
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true">
        <PanelHead title="Итоги звонка" onBack={onClose} />
        <div className="dialog-body">{body}</div>
        {actions}
      </div>
    </div>
  );
}
